import { ReactNode, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { CustomIcon } from "components/icon/customIcon.component";
import {
  V2_INPUT_HEIGHT_PX,
  V2_INPUT_WIDTH_PX,
} from "components/designV2/constants";

export type InputFieldState =
  | "inactive"
  | "disabled"
  | "active"
  | "error"
  | "filled";

export type InputFieldSize = "default" | "compact";

let inputFieldCount = 0;

interface InputFieldProps {
  ariaLabel?: string;
  label?: string;
  value?: string;
  placeholder?: string;
  type?: "text" | "password" | "email" | "number";
  disabled?: boolean;
  hasError?: boolean;
  hintText?: string;
  errorText?: string;
  maxLength?: number;
  autoComplete?: string;
  spellCheck?: boolean;
  fullWidth?: boolean;
  size?: InputFieldSize;
  icon?: ReactNode;
  iconPosition?: "left" | "right";
  iconSize?: string;
  iconAriaLabel?: string;
  iconTabbable?: boolean;
  onIconClick?: () => void;
  iconName?: string;
  onClear?: () => void;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

/**
 * Input field with optional label, validation state, and clear/eye icon.
 */
export const InputField = ({
  ariaLabel,
  label,
  value,
  placeholder,
  type = "text",
  disabled,
  hasError,
  hintText,
  errorText,
  maxLength,
  autoComplete,
  spellCheck,
  fullWidth = false,
  size = "default",
  icon,
  iconPosition,
  iconSize = "24px",
  iconAriaLabel,
  iconTabbable = false,
  onIconClick,
  iconName,
  onClear,
  onChange,
  onFocus,
  onBlur,
}: InputFieldProps): React.ReactElement => {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const inputIdRef = useRef<string>();

  if (!inputIdRef.current) {
    inputFieldCount += 1;
    inputIdRef.current = `input-field-${inputFieldCount}`;
  }

  const inputId = inputIdRef.current;
  const isFilled = Boolean(value && value.length > 0);
  const resolvedErrorText = errorText ?? t("DesignComponents.DefaultError");

  const visualState = useMemo<InputFieldState>(() => {
    if (disabled) return "disabled";
    if (hasError) return "error";
    if (isFocused) return "active";
    if (isFilled) return "filled";
    return "inactive";
  }, [disabled, hasError, isFocused, isFilled]);

  const resolvedIconName = iconName;
  const resolvedIconSize = iconSize;
  const resolvedIconAriaLabel = iconAriaLabel;
  const resolvedOnIconClick = onIconClick;
  const resolvedIconPosition = iconPosition ?? "left";
  const hasCustomIcon = Boolean(icon) || Boolean(iconName);
  const shouldShowDefaultIcon =
    !hasCustomIcon && size !== "compact" && (disabled || isFilled);
  const shouldShowCustomIcon = hasCustomIcon && size !== "compact";
  const defaultIconName = disabled ? "v2/eye" : "v2/cross";
  const handleClear = (): void => {
    if (disabled) {
      return;
    }

    onClear?.();
    onChange?.("");
  };
  const isCustomIconClickable = Boolean(resolvedOnIconClick) && !disabled;
  const isDefaultIconClickable = !disabled && isFilled;

  const handleFocus = (): void => {
    if (disabled) return;
    setIsFocused(true);
    onFocus?.();
  };

  const handleBlur = (): void => {
    setIsFocused(false);
    onBlur?.();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onChange?.(event.target.value);
  };

  return (
    <Container aria-disabled={disabled} $fullWidth={fullWidth} $size={size}>
      {label && <Label htmlFor={inputId}>{label}</Label>}
      <FieldWrapper $state={visualState} $size={size}>
        <Input
          id={inputId}
          type={type}
          aria-label={ariaLabel}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          autoComplete={autoComplete}
          spellCheck={spellCheck}
          $state={visualState}
          $size={size}
          $type={type}
          $hasRightIcon={
            shouldShowDefaultIcon ||
            (shouldShowCustomIcon && resolvedIconPosition === "right")
          }
          $hasLeftIcon={shouldShowCustomIcon && resolvedIconPosition === "left"}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {shouldShowCustomIcon && (
          <IconWrapper
            $position={resolvedIconPosition}
            type="button"
            tabIndex={iconTabbable ? 0 : -1}
            disabled={!isCustomIconClickable}
            aria-label={
              isCustomIconClickable ? resolvedIconAriaLabel : undefined
            }
            onClick={resolvedOnIconClick}
            $clickable={isCustomIconClickable}
          >
            {icon ?? (
              <CustomIcon
                name={resolvedIconName ?? ""}
                size={resolvedIconSize}
                color={ColorV2.Blue5}
              />
            )}
          </IconWrapper>
        )}
        {shouldShowDefaultIcon && (
          <IconWrapper
            $position="right"
            type="button"
            tabIndex={-1}
            disabled={!isDefaultIconClickable}
            onMouseDown={(event) => {
              event.preventDefault();
            }}
            onClick={handleClear}
            $clickable={isDefaultIconClickable}
            aria-label={t("DesignComponents.Clear")}
          >
            <CustomIcon
              name={defaultIconName}
              size="24px"
              color={
                visualState === "error" ? ColorV2.NegativeRed : ColorV2.Blue5
              }
            />
          </IconWrapper>
        )}
      </FieldWrapper>
      {(visualState === "error" ? resolvedErrorText : hintText) && (
        <Message $state={visualState}>
          {visualState === "error" ? resolvedErrorText : hintText}
        </Message>
      )}
    </Container>
  );
};

const COMPACT_INPUT_HEIGHT_PX = 40;
const COMPACT_INPUT_WIDTH_PX = 115;
const COMPACT_INPUT_PADDING_X_PX = 12;

const Container = styled.div<{ $fullWidth: boolean; $size: InputFieldSize }>`
  width: ${(props) =>
    props.$fullWidth
      ? "100%"
      : props.$size === "compact"
        ? `${COMPACT_INPUT_WIDTH_PX}px`
        : `${V2_INPUT_WIDTH_PX}px`};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue4};
`;

const FieldWrapper = styled.div<{
  $state: InputFieldState;
  $size: InputFieldSize;
}>`
  display: flex;
  align-items: center;
  position: relative;
  height: ${(props) =>
    props.$size === "compact"
      ? `${COMPACT_INPUT_HEIGHT_PX}px`
      : `${V2_INPUT_HEIGHT_PX}px`};
  padding: 0
    ${(props) =>
      props.$size === "compact" ? `${COMPACT_INPUT_PADDING_X_PX}px` : "16px"};
  border-radius: ${(props) => (props.$size === "compact" ? "12px" : "16px")};
  background-color: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;

  ${(props) =>
    props.$state === "active" &&
    css`
      border-color: ${ColorV2.Blue1};
    `}

  ${(props) =>
    props.$state === "error" &&
    css`
      border-color: ${ColorV2.NegativeRed};
    `}

  ${(props) =>
    props.$state === "disabled" &&
    css`
      border-color: ${ColorV2.Blue6};
      background-color: ${ColorV2.Blue7};
    `}
`;

const Input = styled.input<{
  $state: InputFieldState;
  $size: InputFieldSize;
  $type: string;
  $hasRightIcon: boolean;
  $hasLeftIcon: boolean;
}>`
  ${TypographyV2.b2}
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  color: ${ColorV2.Blue1};
  padding-right: ${(props) => (props.$hasRightIcon ? "36px" : "0")};
  padding-left: ${(props) => (props.$hasLeftIcon ? "36px" : "0")};

  ${(props) =>
    props.$size === "compact" &&
    props.$type === "number" &&
    css`
      appearance: textfield;

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    `}

  &::placeholder {
    color: ${ColorV2.Blue5};
  }

  ${(props) =>
    props.$state === "inactive" &&
    css`
      color: ${ColorV2.Blue5};
    `}

  ${(props) =>
    props.$state === "active" &&
    css`
      color: ${ColorV2.Blue1};
    `}

  ${(props) =>
    props.$state === "error" &&
    css`
      color: ${ColorV2.NegativeRed};
    `}

  ${(props) =>
    props.$state === "disabled" &&
    css`
      color: ${ColorV2.Blue5};
      cursor: not-allowed;
    `}

  ${(props) =>
    props.$state === "filled" &&
    css`
      color: ${ColorV2.Blue1};
    `}
`;

const IconWrapper = styled.button<{
  $clickable?: boolean;
  $position: "left" | "right";
}>`
  position: absolute;
  ${(props) => (props.$position === "left" ? "left: 12px;" : "right: 12px;")}
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  padding: 0;

  &:focus-visible {
    outline: 2px solid ${ColorV2.Blue3};
    outline-offset: 2px;
    border-radius: 6px;
  }

  ${(props) =>
    props.$clickable &&
    css`
      cursor: pointer;
    `}
`;

const Message = styled.span<{ $state: InputFieldState }>`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue5};

  ${(props) =>
    props.$state === "error" &&
    css`
      color: ${ColorV2.NegativeRed};
    `}
`;
